import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/AuthContext";
import { isLoginExemptPath } from "../../utils/publicAccessPaths";
import { bindGameResync } from "../../utils/bindGameResync";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

// Tracks server-side MYM force (see settings `mym_titles_config` forceModal)
let activeForcedTileId = null;

/**
 * MymForceNavigator
 *
 * Persisted force lives in settings. Live sockets dispatch `mym-force-open` / `mym-force-clear`.
 * We hydrate from the public API once per SPA session (not on every route change) to avoid
 * navigate ↔ location ↔ effect loops and duplicate nested game modals.
 */
const MymForceNavigator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthContext();

  const persistedHydrateRanRef = useRef(false);
  const hydratedForRef = useRef("");
  const forceSyncGenRef = useRef(0);

  const shouldBlockForce = useCallback(() => {
    if (window.top !== window) return true;
    const path = window.location.pathname || "";
    if (isLoginExemptPath(path)) return true;
    if (user && user.user_type === "admin") return true;
    return false;
  }, [user]);

  // Stable: read URL from window at call time so this callback is not recreated on every navigation.
  const syncForceFromServer = useCallback(async () => {
    if (shouldBlockForce()) return;
    const gen = ++forceSyncGenRef.current;

    try {
      const res = await fetch(`${API_BASE}/api/settings/public/mym-titles-config`);
      const data = await res.json().catch(() => ({}));
      if (gen !== forceSyncGenRef.current) return;
      const forcedItem =
        data && Array.isArray(data.config) && data.config.find((c) => c.forceModal);

      if (!forcedItem?.id) {
        const params = new URLSearchParams(window.location.search || "");
        const hasStaleForceUrl = params.has("forceId");
        if (activeForcedTileId !== null || hasStaleForceUrl) {
          activeForcedTileId = null;
          window.dispatchEvent(new Event("mym-force-clear"));
        }
        return;
      }

      activeForcedTileId = forcedItem.id;

      const currentPath = (window.location.pathname || "").toLowerCase();
      const params = new URLSearchParams(window.location.search || "");
      const existingId = params.get("forceId");

      if (currentPath === "/mindyourmusic" && String(existingId) === String(forcedItem.id)) {
        return;
      }

      params.set("forceId", forcedItem.id);

      if (currentPath === "/mindyourmusic") {
        navigate(`/mindyourmusic?${params.toString()}`, { replace: true });
      } else {
        navigate(`/mindyourmusic?${params.toString()}`);
      }
    } catch {
      // ignore
    }
  }, [shouldBlockForce, navigate]);

  // Live socket → DOM events (from ActiveUserTracker)
  useEffect(() => {
    const handler = (event) => {
      const tileId = event?.detail?.id;
      if (!tileId) return;
      if (shouldBlockForce()) return;

      activeForcedTileId = tileId;

      const currentPath = (window.location.pathname || "").toLowerCase();
      const currentParams = new URLSearchParams(window.location.search || "");
      if (
        currentPath === "/mindyourmusic" &&
        String(currentParams.get("forceId")) === String(tileId)
      ) {
        return;
      }

      const params = new URLSearchParams(window.location.search || "");
      params.set("forceId", tileId);

      navigate(`/mindyourmusic?${params.toString()}`, { replace: true });
    };

    const clearHandler = () => {
      activeForcedTileId = null;

      const params = new URLSearchParams(window.location.search || "");
      if (!params.has("forceId")) return;

      params.delete("forceId");
      const search = params.toString();
      const path = window.location.pathname || "/";
      navigate(search ? `${path}?${search}` : path, { replace: true });
    };

    window.addEventListener("mym-force-open", handler);
    window.addEventListener("mym-force-clear", clearHandler);
    return () => {
      window.removeEventListener("mym-force-open", handler);
      window.removeEventListener("mym-force-clear", clearHandler);
    };
  }, [navigate, shouldBlockForce]);

  // Hydrate after login/logout, and again whenever the player returns to the tab.
  useEffect(() => {
    if (shouldBlockForce()) return undefined;

    const key = `${user?.id || "anon"}:${user?.user_type || ""}`;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      if (hydratedForRef.current === key && persistedHydrateRanRef.current) return;
      hydratedForRef.current = key;
      persistedHydrateRanRef.current = true;
      void syncForceFromServer();
    }, 250);

    const unbind = bindGameResync(() => {
      void syncForceFromServer();
    });

    return () => {
      cancelled = true;
      clearTimeout(t);
      unbind();
    };
  }, [shouldBlockForce, syncForceFromServer, user?.id, user?.user_type]);

  // Enforce routing while force is active (no API calls).
  useEffect(() => {
    if (shouldBlockForce()) return;
    if (!activeForcedTileId) return;

    const currentPath = location.pathname || "";
    const params = new URLSearchParams(location.search || "");
    const existingId = params.get("forceId");

    if (
      currentPath.toLowerCase() === "/mindyourmusic" &&
      String(existingId) === String(activeForcedTileId)
    ) {
      return;
    }

    params.set("forceId", activeForcedTileId);

    if (currentPath.toLowerCase() === "/mindyourmusic") {
      navigate(`/mindyourmusic?${params.toString()}`, { replace: true });
    } else {
      navigate(`/mindyourmusic?${params.toString()}`);
    }
  }, [location.pathname, location.search, navigate, shouldBlockForce]);

  return null;
};

export default MymForceNavigator;
